import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaGem, FaLink, FaTicketAlt, FaClock } from "react-icons/fa";
import LiquidGlass from "./LiquidGlass";

const getButtonProps = (task, buttonLoading, handlers) => {
  const {
    handleJoin,
    handleVerify,
    handleShare,
    handleShareStory,
    handleLinkClick,
    handleExternal,
    handleExternalCode,
    setModalOpen,
    setTaskVerify,
    handleBoost,
    handleBoostVerify,
    handleClaim,
    handleTonWalletConnect,
    handleTonWalletVerify,
    handleTonCheckIn,
    handleTelepayVisit,
    handleTelepayVisitVerify,
    handleTelepayTopup,
    handleTelepayTopupVerify,
  } = handlers;

  let text = "";
  let disabled = buttonLoading;
  let onClick = () => {};
  let className =
    "text-sm px-3 py-2 rounded-md text-white w-20 text-center block";

  if (task.status === "pending") {
    text = "Wait";
    disabled = true;
    className += " bg-yellow-500 opacity-50 cursor-not-allowed";
  } else if (task.status === "completed") {
    text = "Done";
    disabled = true;
    className += " bg-gray-600 opacity-50 cursor-not-allowed";
  } else if (task.status === "banned") {
    text = "Ineligible";
    disabled = true;
    className += " bg-red-500 opacity-50 cursor-not-allowed";
  } else if (
    task.status === "verified" ||
    task.status === "boosted" ||
    task.status === "coded"
  ) {
    text = "Claim";
    onClick = () => handleClaim(task);
    className += " bg-[#0C8BC4] hover:bg-[#0a75a3]";
  } else if (task.type === "channelJoin") {
    if (!task.hasJoined) {
      text = "Join";
      onClick = () => handleJoin(task);
      className += " bg-fuchsia-500 hover:bg-fuchsia-600";
    } else {
      text = "Verify";
      onClick = () => handleVerify(task);
      className += " bg-fuchsia-500 hover:bg-fuchsia-600";
    }
  } else if (task.type === "share" || task.type === "shareStory") {
    text = "Share";
    onClick = () => {
      if (task.type === "share") {
        handleShare(task, task.status, task.URL, task.caption);
      } else {
        handleShareStory(task, task.status);
      }
    };
    className += " bg-fuchsia-500 hover:bg-fuchsia-600";
  } else if (task.type === "linkClick" || task.type === "external") {
    text = "Click";
    onClick = () => {
      if (task.type === "linkClick") {
        handleLinkClick(task, task.status);
      } else {
        handleExternal(task, task.status);
      }
    };
    className += " bg-fuchsia-500 hover:bg-fuchsia-600";
  } else if (task.type === "externalCode") {
    if (task.status === "code") {
      text = "Enter Code";
      onClick = () => {
        setModalOpen(true);
        setTaskVerify({ taskId: task._id, bscode: task.bscode });
      };
      className += " bg-fuchsia-500 hover:bg-fuchsia-600";
    } else {
      text = "Click";
      onClick = () => handleExternalCode(task, task.status);
      className += " bg-fuchsia-500 hover:bg-fuchsia-600";
    }
  } else if (task.type === "boost") {
    if (!task.hasJoined) {
      text = "Boost";
      onClick = () => handleBoost(task, task.status);
      className += " bg-fuchsia-500 hover:bg-fuchsia-600";
    } else {
      text = "Verify";
      onClick = () => handleBoostVerify(task);
      className += " bg-fuchsia-500 hover:bg-fuchsia-600";
    }
  } else if (task.type === "tonWalletConnect") {
    if (task.status === "pending_ton") {
      text = "Verify";
      onClick = () => handleTonWalletVerify(task);
      className += " bg-[#0088cc] hover:bg-[#0077b3]";
    } else {
      text = "Connect";
      onClick = () => handleTonWalletConnect(task);
      className += " bg-[#0088cc] hover:bg-[#0077b3]";
    }
  } else if (task.type === "tonCheckin") {
    text = "Check In";
    onClick = () => handleTonCheckIn(task);
    className += " bg-[#0088cc] hover:bg-[#0077b3]";
  } else if (task.type === "telepay_visit") {
    if (!task.hasJoined) {
      text = "Go";
      onClick = () => handleTelepayVisit(task);
      className += " bg-sky-500 hover:bg-sky-600";
    } else {
      text = "Verify";
      onClick = () => handleTelepayVisitVerify(task);
      className += " bg-sky-500 hover:bg-sky-600";
    }
  } else if (task.type === "telepay_topup") {
    if (!task.hasJoined) {
      text = "Go";
      onClick = () => handleTelepayTopup(task);
      className += " bg-emerald-500 hover:bg-emerald-600";
    } else {
      text = "Verify";
      onClick = () => handleTelepayTopupVerify(task);
      className += " bg-emerald-500 hover:bg-emerald-600";
    }
  } else if (task.type === "manus_referral") {
    if (task.status === "code") {
      text = "Enter Email";
      onClick = () => {
        setModalOpen(true);
        setTaskVerify({ taskId: task._id, isManus: true });
      };
      className += " bg-fuchsia-500 hover:bg-fuchsia-600";
    } else {
      text = "Open Link";
      onClick = () => handlers.handleManusReferral(task, task.status);
      className += " bg-fuchsia-500 hover:bg-fuchsia-600";
    }
  } else {
    text = "Action";
    className += " bg-gray-500 opacity-50 cursor-not-allowed";
    disabled = true;
  }

  return { text, disabled, onClick, className };
};

const TaskCard = ({ task, buttonLoading, handlers }) => {
  const { text, disabled, onClick, className } = getButtonProps(
    task,
    buttonLoading,
    handlers
  );

  return (
    <motion.div
      key={task._id}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <LiquidGlass
        className={`rounded-2xl my-2 shadow-sm border-2 overflow-hidden transition-colors duration-300 ${
          task.isFeatured ? "border-sky-300 dark:border-sky-400" : "border-slate-200 dark:border-slate-700"
        }`}
        contentClassName="w-full"
        glassColor={
          task.isFeatured
            ? {
                from: "from-sky-50/90 dark:from-sky-950/90",
                via: "via-white/80 dark:via-slate-900/80",
                to: "to-white/70 dark:to-slate-900/70",
                overlayFrom: "from-sky-400/20",
                overlayVia: "via-blue-400/10",
                overlayTo: "to-pink-400/10",
                glowFrom: "from-white/10",
                glowTo: "to-transparent",
              }
            : {
                from: "from-white/90 dark:from-slate-900/90",
                via: "via-slate-50/80 dark:via-slate-800/80",
                to: "to-white/70 dark:to-slate-900/70",
                overlayFrom: "from-blue-400/10",
                overlayVia: "via-purple-400/10",
                overlayTo: "to-pink-400/10",
                glowFrom: "from-white/5 dark:from-white/10",
                glowTo: "to-transparent",
              }
        }
      >
        {/* Ending Soon banner — top stripe */}
        {task.isTimeLimited && (
          <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-semibold text-center py-1 px-3 tracking-wide flex items-center justify-center gap-1">
            <FaClock className="inline" size={10} />
            Ending Soon
          </div>
        )}

        {/* Card body */}
        <div className="flex justify-between items-start p-4">
          {/* Left Column */}
          <div className="flex-grow mr-4">
            <h3
              className="text-slate-900 dark:text-white font-semibold"
              style={{ textShadow: "0px 0px 4px rgba(0, 0, 0, 0.2)" }}
            >
              {task.name}
            </h3>

            {task.desc && (
              <p
                className="text-slate-500 dark:text-gray-400 text-sm mt-1"
                style={{ textShadow: "0px 0px 3px rgba(0, 0, 0, 0.1)" }}
              >
                {task.desc}
              </p>
            )}

            {/* Reward pills */}
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <span className="inline-flex items-center gap-1 bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 text-xs font-semibold px-2 py-0.5 rounded-full">
                <FaGem size={10} />
                {task.points}
              </span>
              {task.tickets > 0 && (
                <span className="inline-flex items-center gap-1 bg-red-100 dark:bg-red-900/40 text-red-600 dark:text-red-300 text-xs font-semibold px-2 py-0.5 rounded-full">
                  <FaTicketAlt size={10} />
                  {task.tickets}
                </span>
              )}
            </div>

            {task.guide && (
              <a
                href={task.guide}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-500 dark:text-blue-300 text-sm mt-2 inline-flex items-center gap-1 animate-pulse"
              >
                <FaLink size={11} /> Guide - လမ်းညွှန်ဖတ်ပါ
              </a>
            )}

            {/* Status badges */}
            {task.status === "unverified" && (
              <span className="mt-2 inline-flex items-center gap-1 bg-red-600 dark:bg-red-700 text-white text-xs font-semibold px-3 py-0.5 rounded-full shadow-sm">
                ✗ Verification failed
              </span>
            )}
            {task.status === "pending" && (
              <span className="mt-2 inline-flex items-center gap-1 bg-amber-500 dark:bg-yellow-500 text-white text-xs font-semibold px-3 py-0.5 rounded-full shadow-sm">
                <FaClock size={10} /> Wait 12–24hrs for verification
              </span>
            )}
          </div>

          {/* Right Column — button */}
          <div className="flex-shrink-0 flex flex-col items-center space-y-1">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className={className}
              onClick={onClick}
              disabled={disabled}
            >
              {buttonLoading ? (
                <svg
                  className="animate-spin h-5 w-5 text-white mx-auto"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V4a10 10 0 00-10 10h2zm2 0a6 6 0 016-6V4a8 8 0 00-8 8h2zm2 0a4 4 0 014-4V4a6 6 0 00-6 6h2zm2 0a2 2 0 012-2V4a4 4 0 00-4 4h2z"
                  ></path>
                </svg>
              ) : (
                text
              )}
            </motion.button>

            {task.type === "externalCode" && task.status === "code" && (
              <p
                onClick={() => handlers.handleExternalCode(task, task.status)}
                className="cursor-pointer text-blue-600 dark:text-blue-500 hover:text-blue-700 text-sm font-bold underline"
              >
                Click
              </p>
            )}

            {task.type === "manus_referral" && task.status === "code" && (
              <p
                onClick={() => handlers.handleManusReferral(task, task.status)}
                className="cursor-pointer text-blue-600 dark:text-blue-500 hover:text-blue-700 text-sm font-bold underline"
              >
                Reopen
              </p>
            )}
          </div>
        </div>
      </LiquidGlass>
    </motion.div>
  );
};

export default React.memo(TaskCard);
